/**
 * Strips one level of HTML entity so extracted text reads normally.
 *
 * Named entities only — numeric ones are resolved by code point instead, so the
 * table does not have to list them in both notations.
 */
const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&apos;': "'",
  '&nbsp;': ' ',
};

/**
 * One entity as text.
 *
 * Numeric entities are decoded in both notations: serializers emit an
 * apostrophe as `&#39;` or `&#x27;` about equally, and the hex form is not
 * something to leave sitting in the summary as literal text.
 *
 * An entity that is neither is left as it was: replacing the unknown with a
 * space silently eats the character, and the entity is at least recognisable.
 */
function decodeEntity(entity: string): string {
  const named = ENTITIES[entity.toLowerCase()];
  if (named) return named;

  const numeric = /^&#(x)?([0-9a-f]+);$/i.exec(entity);
  if (numeric) {
    const codePoint = parseInt(numeric[2], numeric[1] ? 16 : 10);
    // Surrogates and out-of-range values would throw.
    if (
      Number.isFinite(codePoint) &&
      codePoint > 0 &&
      codePoint <= 0x10ffff &&
      !(codePoint >= 0xd800 && codePoint <= 0xdfff)
    ) {
      return String.fromCodePoint(codePoint);
    }
  }

  return entity;
}

/**
 * A short description of a draft that has no `summary` of its own. Takes the
 * opening of the body as plain text and lets the row clamp it to two lines.
 *
 * Deliberately not a parser: this runs per row, the input is the AI's own
 * generated markup, and the result is truncated anyway.
 */
export function toSummary(htmlContent: string, maxLength = 200): string {
  const text = htmlContent
    // Treat block boundaries as spaces so words don't run together.
    .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&[a-z]+;|&#x?[0-9a-f]+;/gi, decodeEntity)
    .replace(/\s+/g, ' ')
    .trim();

  if (text.length <= maxLength) return text;
  // Cut on a word boundary so the clamp doesn't land mid-word.
  const cut = text.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(' ');
  return `${cut.slice(0, lastSpace > 0 ? lastSpace : maxLength)}…`;
}
